import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocaleLink as Link } from "../../i18n/LocaleLink";
import BrassMonogram from "./BrassMonogram";
import PrimaryNav from "./PrimaryNav";
import { SettingsCrown } from "../watch";
import { getActiveChartId } from "../../lib/chartSession";

interface Props {
  chartId?: string | null;
  dense?: boolean;
  /** Hide the 5-tab strip (e.g. legal pages that want minimal chrome). */
  hidePrimaryNav?: boolean;
}

/**
 * AppHeader — redesign 2026-07-09.
 *
 * Replaces the Discover/Predict/Learn/Tools mega-menus with the 5-tab
 * PrimaryNav (Today · Watch · Ask · Listen · Discover). Settings live on
 * the crown. Wordmark home-links to living Today (`/`).
 */
export default function AppHeader({
  chartId,
  dense = false,
  hidePrimaryNav = false,
}: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const resolvedChartId = chartId ?? getActiveChartId();
  const accountActive = pathname.startsWith("/account") || pathname.startsWith("/plans");

  return (
    // Task 2 §10.3 (landmark structure). A <header> element only resolves
    // to the implicit `banner` landmark when it is a direct child of <body>.
    // Because every page mounts AppHeader INSIDE its <main> (and React
    // Router wraps everything in a root div besides), the implicit role
    // never resolves. The explicit role below guarantees the banner
    // landmark on every page without a mechanical 30-page refactor that
    // would risk regressing unrelated routes.
    <header className="app-header" role="banner">
      <div
        className="max-w-[1280px] mx-auto flex items-center justify-between gap-3"
        style={{ padding: dense ? "8px 16px" : "10px 20px" }}
      >
        <Link to="/" className="flex items-center gap-2.5 no-underline cursor-pointer shrink-0">
          <BrassMonogram size={24} />
          <span className="wordmark font-display italic text-lg text-brass-200 hidden sm:block">
            astrogenesis
          </span>
        </Link>

        {/* Desktop primary tabs sit in the header row */}
        {!hidePrimaryNav && (
          <div className="hidden md:flex flex-1 justify-center min-w-0 px-2">
            <PrimaryNav chartId={resolvedChartId} variant="top" />
          </div>
        )}

        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/plans"
            className={`hidden sm:inline-flex font-caps text-micro tracking-[0.2em] uppercase transition-colors ${
              accountActive ? "text-brass-200" : "text-parchment-400 hover:text-parchment-100"
            }`}
          >
            {t("ui.web.landing.plans")}
          </Link>
          <Link to="/android" className="inline-flex min-h-11 items-center text-sm text-brass-200">{t("ui.web.android.nav", { lng: "en" })}</Link>
          <SettingsCrown />
        </div>
      </div>

      {/* Mobile bottom tab bar (fixed via CSS) */}
      {!hidePrimaryNav && (
        <div className="md:hidden">
          <PrimaryNav chartId={resolvedChartId} variant="bottom" />
        </div>
      )}
    </header>
  );
}
